<script lang="ts">
export default {
  name: 'UISpecification',
}
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export interface Props {
  label: string;
  text?: string | number;
  icon?: [string, string] | string;
  isTruncatable?: boolean;
  isDisabled?: boolean;
  fetching?: boolean;
  size?: 'base' | 'lg' | 'small';
  direction?: 'default' | 'horizontal' | 'vertical' | 'compact';
  theme?: 'default' | 'dark' | 'white';
}

const props = withDefaults(defineProps<Props>(), {
  icon: undefined,
  isTruncatable: false,
  isDisabled: false,
  fetching: false,
  text: '',
  size: 'base',
  direction: 'default',
  theme: 'default',
});

const iconColor = computed(() => {
  if (props.theme === 'white') return '#fff';

  return undefined;
});
</script>

<template>
  <div
    data-test-id="specification"
    class="flex"
    :class="{
      'flex-col': props.direction === 'vertical',
      'items-center': props.direction !== 'vertical',
      'w-full': props.direction === 'horizontal',
      'opacity-50': props.isDisabled,
    }"
  >
    <div
      v-if="icon"
      :class="{
        'size-8 mr-4 shrink-0': props.size === 'lg',
        'size-6 mr-2 w-5': props.size === 'base',
        'mt-1 mr-2 size-4': props.size === 'small',
        'mr-4': props.direction !== 'vertical' && props.size === 'lg',
        'mb-2': props.direction === 'vertical',
      }"
    >
      <FontAwesomeIcon
        :icon="Array.isArray(props.icon) ? props.icon : ['fad', props.icon]"
        class="text-xl"
        :class="{
          'size-8 shrink-0 mr-4 text-2xl': props.size === 'lg',
          'w-5': props.size === 'base',
        }"
        :style="{ '--fa-primary-color': iconColor, '--fa-secondary-color': iconColor }"
      />
    </div>
    <div
      class="flex flex-col w-full"
      :class="{
        'truncate': isTruncatable,
        'grid grid-cols-2 gap-2 md:gap-4 items-center': props.direction === 'horizontal',
      }"
      >
      <UISkeleton :fetching="props.fetching" text>
        <p
          class="text-sm"
          :class="{
            'whitespace-nowrap': props.direction !== 'vertical' && props.direction !== 'compact',
            'text-gray-700': props.direction === 'compact' && props.theme !== 'dark',
          }"
        >
          {{ props.label }}
        </p>
      </UISkeleton>
      <UISkeleton :fetching="props.fetching" text>
        <p
          class="font-semibold text-blue-900"
          :class="[
            {'truncate whitespace-nowrap': isTruncatable},
            {'text-right text-base': props.direction === 'horizontal'},
            {'text-lg': size === 'lg'}
          ]"
        >
          <slot>
            {{ props.text }}
          </slot>
        </p>
      </UISkeleton>
    </div>
  </div>
</template>
